import React from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { TagPill } from '@aibuild/ui';
import { listLocalRunners } from '@api/localRunners';
import type { AppRunController } from '../appFrame/useAppRunController';

const RUNNER_NAMES_KEY = ['local-runners', 'names'] as const;

interface RunnerTagProps {
  readonly controller: AppRunController;
}

const RunnerTag: React.FC<RunnerTagProps> = ({ controller }) => {
  const intl = useIntl();
  const run = controller.run;
  const runner = run?.runner ?? null;
  const fromSetup = runner
    ? controller.runSetup.setup?.runners.find((candidate) => candidate.vmId === runner)?.name
    : undefined;
  // The run setup follows the app as it is now, so it lists no runners for a run made before the app moved to the cloud.
  const runners = useQuery({
    queryKey: RUNNER_NAMES_KEY,
    queryFn: listLocalRunners,
    enabled: Boolean(runner) && !fromSetup && !run?.readOnly,
    staleTime: 60_000,
  });
  if (!runner) return null;
  const name = fromSetup ?? runners.data?.find((candidate) => candidate.vmId === runner)?.name;
  return (
    <TagPill
      label={
        name
          ? intl.formatMessage({ id: 'apps.run.runnerTag', defaultMessage: 'On {runner}' }, { runner: name })
          : intl.formatMessage({ id: 'apps.run.runnerTag.unnamed', defaultMessage: 'On your computer' })
      }
      prefix={false}
      size="sm"
      dataTestId="app-run__runner-tag"
    />
  );
};

export default RunnerTag;
